import { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  AlertTitle,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ContentPaste as PasteIcon,
  ContentCopy as CopyIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import {
  validateImportSchema,
  resolvePackage,
  flattenToPlaceholders,
  validateForGeneration,
  buildGenerationPayload,
  validateForEngagementLetter,
  buildEngagementLetterPayload,
  validateForPlanningConfirmation,
  buildPlanningConfirmationPayload,
  ENGAGEMENT_LETTER_TEMPLATE_PATHS,
} from '../services/briefImportMapper';
import { mapOperatingAgreementImport } from '../services/operatingAgreementImportMapper';
import MatterFactsForm from './MatterFactsForm';

// ---------------------------------------------------------------------------
// Field display config — groups for the review UI (read-only)
// ---------------------------------------------------------------------------
const FIELD_GROUPS = [
  {
    label: 'Client Information',
    fields: [
      { key: 'client', label: 'Client Name' },
      { key: 'client_residence_county', label: 'County of Residence' },
      { key: 'spouse', label: 'Spouse Name' },
      { key: 'client_children_names', label: 'Children' },
      { key: 'client_date_of_birth', label: 'Date of Birth' },
    ],
  },
  {
    label: 'Executor',
    fields: [
      { key: 'executor_name', label: 'Executor' },
      { key: 'executor_relationship', label: 'Executor Relationship' },
      { key: 'successor_executor_name', label: 'Successor Executor' },
      { key: 'successor_executor_relationship', label: 'Succ. Executor Relationship' },
      { key: 'second_successor_executor_name', label: '2nd Successor Executor' },
      { key: 'second_successor_executor_relationship', label: '2nd Succ. Executor Relationship' },
    ],
  },
  {
    label: 'Guardian (if minor children)',
    fields: [
      { key: 'guardian_name', label: 'Guardian' },
      { key: 'guardian_relationship', label: 'Guardian Relationship' },
      { key: 'successor_guardian_name', label: 'Successor Guardian' },
      { key: 'successor_guardian_relationship', label: 'Succ. Guardian Relationship' },
    ],
  },
  {
    label: 'Successor Trustee',
    fields: [
      { key: 'successor_trustee_name', label: '1st Successor Trustee' },
      { key: 'successor_trustee_relationship', label: '1st Succ. Trustee Relationship' },
      { key: 'second_successor_trustee_name', label: '2nd Successor Trustee' },
      { key: 'second_successor_trustee_relationship', label: '2nd Succ. Trustee Relationship' },
      { key: 'third_successor_trustee_name', label: '3rd Successor Trustee' },
      { key: 'third_successor_trustee_relationship', label: '3rd Succ. Trustee Relationship' },
    ],
  },
  {
    label: 'Trust Protector',
    fields: [
      { key: 'first_successor_trust_protector_name', label: '1st Trust Protector' },
      { key: 'first_successor_trust_protector_relationship', label: '1st Trust Protector Relationship' },
      { key: 'second_successor_trust_protector_name', label: '2nd Trust Protector' },
      { key: 'second_successor_trust_protector_relationship', label: '2nd Trust Protector Relationship' },
    ],
  },
  {
    label: 'Estate Protector',
    fields: [
      { key: 'estate_protector', label: 'Estate Protector' },
    ],
  },
  {
    label: 'Financial Power of Attorney',
    fields: [
      { key: 'agent_name', label: 'Financial Agent' },
      { key: 'agent_relationship', label: 'Financial Agent Relationship' },
      { key: 'successor_agent_name', label: 'Successor Financial Agent' },
      { key: 'successor_agent_relationship', label: 'Succ. Financial Agent Relationship' },
    ],
  },
  {
    label: 'Healthcare Power of Attorney',
    fields: [
      { key: 'healthcare_agent_name', label: 'Healthcare Agent' },
      { key: 'healthcare_agent_street_address', label: 'HC Agent Address' },
      { key: 'healthcare_agent_city_and_state_and_zipcode', label: 'HC Agent City/State/Zip' },
      { key: 'healthcare_agent_telephone', label: 'HC Agent Phone' },
      { key: 'first_successor_healthcare_agent_name', label: '1st Succ. HC Agent' },
      { key: 'first_successor_healthcare_agent_street_address', label: '1st Succ. HC Agent Address' },
      { key: 'first_successor_healthcare_agent_city_and_state_and_zipcode', label: '1st Succ. HC Agent City/State/Zip' },
      { key: 'first_successor_healthcare_agent_telephone', label: '1st Succ. HC Agent Phone' },
      { key: 'second_successor_healthcare_agent_name', label: '2nd Succ. HC Agent' },
      { key: 'second_successor_healthcare_agent_street_address', label: '2nd Succ. HC Agent Address' },
      { key: 'second_successor_healthcare_agent_city_and_state_and_zipcode', label: '2nd Succ. HC Agent City/State/Zip' },
      { key: 'second_successor_healthcare_agent_telephone', label: '2nd Succ. HC Agent Phone' },
    ],
  },
  {
    label: 'Dates',
    fields: [
      { key: 'signing_date', label: 'Signing Date' },
      { key: 'rlt_date', label: 'Revocable Living Trust Date' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function BriefImportPage({ onGenerationComplete, onBack }) {
  const [rawJson, setRawJson] = useState('');
  const [parseError, setParseError] = useState(null);
  const [importData, setImportData] = useState(null);
  const [variables, setVariables] = useState(null);
  const [validation, setValidation] = useState(null);
  const [packageType, setPackageType] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [copied, setCopied] = useState(false);
  // Opt-in bundle for the post-execution To-Do List. Populated by
  // MatterFactsForm and forwarded verbatim to buildGenerationPayload.
  const [todoListOptions, setTodoListOptions] = useState({
    generateTodoList: false,
    matterFacts: null,
    spouseMatterFacts: null,
    spousePlanDetails: null,
  });
  // Render target: 'package' runs the full validator and bundles the
  // package documents; 'engagement_letter' runs the narrowly-scoped EL
  // validator (validateForEngagementLetter) and emits a single standalone
  // Engagement Letter document. EL mode is the path the brief-helper agent
  // targets before drafting facts (signing date, RLT date, etc.) are known.
  const [renderTarget, setRenderTarget] = useState('package');

  // --- Parse & Validate ---
  // Accepts an optional renderTarget override so the on-results toggle can
  // immediately re-validate with the new mode (passing the new value through
  // closure-stale state would otherwise read the previous renderTarget).
  const handleParse = useCallback((targetOverride) => {
    // Guard against React passing a click event when this is wired as an
    // onClick handler directly. Only treat the override as authoritative
    // when it is a string (one of 'package' | 'engagement_letter').
    const target = (typeof targetOverride === 'string') ? targetOverride : renderTarget;

    setParseError(null);
    setImportData(null);
    setVariables(null);
    setValidation(null);
    setPackageType(null);
    setGenError(null);
    setCopied(false);

    let json;
    try {
      json = JSON.parse(rawJson);
    } catch (e) {
      setParseError(`Invalid JSON: ${e.message}`);
      return;
    }

    // Branch on schema_name. Operating-agreement-import has a disjoint
    // contract from estate-plan-import; route it through the OA mapper so
    // the EP-specific validation, FIELD_GROUPS review, and MatterFactsForm
    // are bypassed.
    if (json.schema_name === 'operating-agreement-import') {
      if (target === 'engagement_letter') {
        setParseError(
          'Engagement Letter mode is only available for estate-plan-import JSON. ' +
          'Switch the render target back to "Full Package" for operating-agreement-import.'
        );
        return;
      }
      const oa = mapOperatingAgreementImport(json);
      if (oa.errors.length > 0) {
        setParseError(`Operating Agreement validation failed:\n${oa.errors.join('\n')}`);
        return;
      }
      setPackageType(oa.package);
      setVariables(oa.variables);
      setValidation({ canGenerate: true, missingFields: [], warnings: (oa.warnings || []).map(w => ({ field: '', message: w })), correctionPrompt: null });
      setImportData(json);
      return;
    }

    // Schema validation
    const schema = validateImportSchema(json);
    if (!schema.valid) {
      setParseError(`Schema validation failed:\n${schema.errors.join('\n')}`);
      return;
    }

    // Package resolution. Skipped for Planning Confirmation mode because
    // the PC sub-brief is package-agnostic — it carries plan_assumptions
    // prose + an optional subset of fiduciaries, and doesn't need a
    // package to render.
    let pkg = null;
    if (target !== 'planning_confirmation') {
      const pkgResult = resolvePackage(json);
      if (pkgResult.errors.length > 0) {
        setParseError(`Package error: ${pkgResult.errors.join(' ')}`);
        return;
      }
      pkg = pkgResult.package;
    } else {
      // PC mode: use the brief's package_candidate when present so role
      // titles render correctly (Co-Trustees vs Trustee), otherwise default
      // to Disclaimer for variable computation only.
      pkg = json.package_candidate || 'Disclaimer Package';
    }
    setPackageType(pkg);

    // Flatten to placeholders (used by every render target — the EL and PC
    // still benefit from the canonical fiduciary recap when names are present).
    const vars = flattenToPlaceholders(json, pkg);
    setVariables(vars);

    // Validate against the selected render target. EL and PC validators
    // are narrower than full-package and tolerate missing drafting facts.
    if (target === 'engagement_letter') {
      if (!ENGAGEMENT_LETTER_TEMPLATE_PATHS[pkg]) {
        setParseError(
          `No engagement letter template is registered for "${pkg}". ` +
          `Currently only "Disclaimer Package" has an EL variant. ` +
          `Switch render target to "Full Package" or change the package.`
        );
        return;
      }
      const elVal = validateForEngagementLetter(json);
      setValidation(elVal);
      setImportData(json);
      return;
    }

    if (target === 'planning_confirmation') {
      const pcVal = validateForPlanningConfirmation(json);
      setValidation(pcVal);
      setImportData(json);
      return;
    }

    const val = validateForGeneration(vars, pkg, json);
    setValidation(val);
    setImportData(json);
  }, [rawJson, renderTarget]);

  // --- File Upload ---
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => setRawJson(evt.target.result);
    reader.readAsText(file);
  }, []);

  // --- Clipboard Paste ---
  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setRawJson(text);
    } catch { /* user can paste manually */ }
  }, []);

  // --- Copy Correction Prompt ---
  const handleCopyPrompt = useCallback(async () => {
    if (!validation?.correctionPrompt) return;
    try {
      await navigator.clipboard.writeText(validation.correctionPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch { /* fallback: user selects text manually */ }
  }, [validation]);

  // --- Generate ---
  const handleGenerate = useCallback(async () => {
    if (!importData || !packageType || !validation?.canGenerate) return;

    setGenerating(true);
    setGenError(null);

    try {
      const { getCurrentUserInfo } = await import('../utils/auth');
      const { getActiveSessionId } = await import('../utils/sessionManagerV2');
      const { generateDocumentsDirect, getDocumentKey } = await import('../services/documentService');

      const userInfo = await getCurrentUserInfo();
      const sessionId = await getActiveSessionId(userInfo.sub, userInfo.firmId);

      let payload;
      if (importData.schema_name === 'operating-agreement-import') {
        const oa = mapOperatingAgreementImport(importData);
        const packageDocs = Object.keys(oa.documentPaths);
        const placeholders = Object.keys(oa.variables).filter(k => oa.variables[k] !== '' && oa.variables[k] !== null && oa.variables[k] !== undefined);
        payload = {
          documentType: oa.package,
          variables: oa.variables,
          userMessage: '',
          isPackage: true,
          packageDocs,
          packageTemplatePaths: { ...oa.documentPaths },
          templatePath: '',
          placeholders,
          docMetadata: {},
          sessionId,
          userSub: userInfo.sub,
          firmId: userInfo.firmId,
          generateTodoList: false,
        };
      } else if (renderTarget === 'engagement_letter') {
        payload = buildEngagementLetterPayload(
          importData, packageType, sessionId, userInfo.sub, userInfo.firmId
        );
      } else if (renderTarget === 'planning_confirmation') {
        payload = buildPlanningConfirmationPayload(
          importData, sessionId, userInfo.sub, userInfo.firmId
        );
      } else {
        payload = buildGenerationPayload(
          importData, packageType, sessionId, userInfo.sub, userInfo.firmId,
          todoListOptions
        );
      }

      const response = await generateDocumentsDirect(payload);

      // Surface explicit failure responses before trying to extract URLs.
      // generateDocumentsDirect returns { success: false, error, isTimeout? }
      // on API Gateway timeouts and other handled errors. Without this
      // check, the empty `docs` array silently flows through as "0
      // documents generated" with no error visible to the user.
      if (response && response.success === false) {
        const msg = response.isTimeout
          ? 'Generation timed out at the gateway (29s limit). Try again — the document may be cached now and complete faster on retry.'
          : (response.error || 'Document generation failed. Please try again.');
        setGenError(msg);
        return;
      }

      // Transform response into DocumentDownload's expected shape
      const docs = [];
      if (response.documentUrls || response.packageUrls) {
        const urls = response.documentUrls || response.packageUrls;
        Object.entries(urls).forEach(([docType, url]) => {
          const docKey = getDocumentKey(url);
          docs.push({ id: docKey, name: docType, url, key: docKey, content: null, metadata: null, templatePath: null });
        });
      } else if (response.documentUrl) {
        const docKey = getDocumentKey(response.documentUrl);
        docs.push({ id: docKey, name: packageType, url: response.documentUrl, key: docKey, content: null, metadata: null, templatePath: null });
      }

      // If the response had neither URLs nor an explicit failure, treat it
      // as an error rather than reporting "complete" with an empty list.
      if (docs.length === 0) {
        setGenError(
          'Document generation completed but no document URLs were returned. ' +
          'This often means the request timed out at the API gateway while the Lambda kept running. ' +
          'Check CloudWatch for the request id, then retry.'
        );
        return;
      }

      if (onGenerationComplete) {
        onGenerationComplete(docs, sessionId, variables);
      }
    } catch (err) {
      console.error('Generation failed:', err);
      setGenError(err.message || 'Document generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  }, [importData, packageType, validation, onGenerationComplete, todoListOptions, renderTarget]);

  // --- Computed ---
  const missingFieldSet = new Set((validation?.missingFields || []).map(f => f.field));
  const isOA = importData?.schema_name === 'operating-agreement-import';

  const getFieldColor = (fieldKey) => {
    if (missingFieldSet.has(fieldKey)) return 'error.main';
    if (variables?.[fieldKey]) return 'success.main';
    return 'text.disabled';
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {onBack && (
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <BackIcon />
          </IconButton>
        )}
        <Box>
          <Typography variant="h4">Import Plan JSON</Typography>
          <Typography variant="body2" color="text.secondary">
            Paste or upload a reviewed import JSON (estate-plan-import or operating-agreement-import). The JSON must be complete and correct — SED validates but does not modify it.
          </Typography>
        </Box>
      </Box>

      {/* Step 1: JSON Input */}
      {!importData && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Provide Import JSON</Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button variant="outlined" startIcon={<PasteIcon />} onClick={handlePaste} size="small">
              Paste from Clipboard
            </Button>
            <Button variant="outlined" startIcon={<UploadIcon />} component="label" size="small">
              Upload .json File
              <input type="file" accept=".json" hidden onChange={handleFileUpload} />
            </Button>
          </Box>

          <TextField
            multiline fullWidth minRows={12} maxRows={24}
            placeholder='Paste estate-plan-import or operating-agreement-import JSON here...'
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />

          {parseError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>Import Error</AlertTitle>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.85rem' }}>{parseError}</pre>
            </Alert>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Render target
              </Typography>
              <ToggleButtonGroup
                value={renderTarget}
                exclusive
                onChange={(_, v) => v && setRenderTarget(v)}
                size="small"
                aria-label="render target"
              >
                <ToggleButton value="package" aria-label="full package">
                  Full Package
                </ToggleButton>
                <ToggleButton value="engagement_letter" aria-label="engagement letter only">
                  Engagement Letter Only
                </ToggleButton>
                <ToggleButton value="planning_confirmation" aria-label="planning confirmation only">
                  Planning Confirmation Only
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {renderTarget === 'engagement_letter'
                  ? 'Renders only the engagement letter; tolerates missing drafting facts (signing date, RLT date, fiduciaries, etc.).'
                  : renderTarget === 'planning_confirmation'
                  ? 'Renders only the Planning Confirmation; only client name is required. Use for PC sub-briefs or to regenerate after additional info arrives.'
                  : 'Generates the full package of documents; requires all drafting facts in the JSON.'}
              </Typography>
            </Box>
            <Button variant="contained" onClick={handleParse} disabled={!rawJson.trim()} size="large">
              Validate & Review
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 2: Results */}
      {importData && variables && validation && (
        <>
          {/* Package */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6">Package: {packageType}</Typography>
              {renderTarget === 'engagement_letter' && (
                <Chip label="Engagement Letter only" color="primary" size="small" variant="outlined" />
              )}
              {renderTarget === 'planning_confirmation' && (
                <Chip label="Planning Confirmation only" color="primary" size="small" variant="outlined" />
              )}
              {importData.client?.marital_status && (
                <Chip label={importData.client.marital_status} size="small" variant="outlined" />
              )}
              {validation.canGenerate ? (
                <Chip label="Ready to generate" color="success" size="small" icon={<CheckIcon />} />
              ) : (
                <Chip label={`${validation.missingFields.length} missing fields`} color="error" size="small" icon={<ErrorIcon />} />
              )}
            </Box>
            {/* Render target toggle, persistent on the results screen so users
                can switch between Full Package and Engagement Letter Only
                without backing out and re-validating manually. Changing the
                toggle re-runs handleParse against the existing rawJson. */}
            {!isOA && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Render target
                </Typography>
                <ToggleButtonGroup
                  value={renderTarget}
                  exclusive
                  onChange={(_, v) => {
                    if (!v) return;
                    setRenderTarget(v);
                    // Re-run validation immediately with the new render
                    // target. Passing v as an override avoids the
                    // closure-stale read of renderTarget that would
                    // otherwise see the previous value.
                    handleParse(v);
                  }}
                  size="small"
                  aria-label="render target"
                >
                  <ToggleButton value="package" aria-label="full package">
                    Full Package
                  </ToggleButton>
                  <ToggleButton value="engagement_letter" aria-label="engagement letter only">
                    Engagement Letter Only
                  </ToggleButton>
                  <ToggleButton value="planning_confirmation" aria-label="planning confirmation only">
                    Planning Confirmation Only
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {renderTarget === 'engagement_letter'
                    ? 'Renders only the engagement letter; tolerates missing drafting facts.'
                    : renderTarget === 'planning_confirmation'
                    ? 'Renders only the Planning Confirmation; only client name is required.'
                    : 'Generates the full package; requires all drafting facts.'}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Correction Prompt (if issues) */}
          {!validation.canGenerate && validation.correctionPrompt && (
            <Alert severity="error" sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" startIcon={<CopyIcon />} onClick={handleCopyPrompt}>
                  {copied ? 'Copied!' : 'Copy Prompt'}
                </Button>
              }
            >
              <AlertTitle>JSON is incomplete — {validation.missingFields.length} fields missing</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Copy the prompt below and paste it into your AI tool to get corrected JSON.
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                {validation.correctionPrompt}
              </Paper>
            </Alert>
          )}

          {/* Warnings */}
          {validation.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Warnings ({validation.warnings.length})</AlertTitle>
              {validation.warnings.map((w, i) => (
                <Typography key={i} variant="body2">{w.field}: {w.message}</Typography>
              ))}
            </Alert>
          )}

          {/* OA-specific review (compact: company + members + key terms) */}
          {isOA && (
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Operating Agreement Summary</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', rowGap: 0.5, columnGap: 2, fontSize: '0.9rem' }}>
                <Typography variant="body2" color="text.secondary">Company</Typography>
                <Typography variant="body2">{importData.company?.name}</Typography>
                <Typography variant="body2" color="text.secondary">State of Formation</Typography>
                <Typography variant="body2">{importData.company?.state_of_formation || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">Effective Date</Typography>
                <Typography variant="body2">{importData.company?.effective_date || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">Tax Classification</Typography>
                <Typography variant="body2">{importData.tax?.classification}</Typography>
                <Typography variant="body2" color="text.secondary">Members</Typography>
                <Box>
                  {(importData.members || []).map((m, i) => (
                    <Typography key={i} variant="body2">
                      {m.full_name} — {m.percentage_interest}%{m.is_manager ? ' (Co-Manager)' : ''}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary">Stewart Law Represents</Typography>
                <Typography variant="body2">{importData.representation?.represented_member_name || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">Deliverables</Typography>
                <Typography variant="body2">
                  Operating Agreement
                  {importData.deliverables?.include_summary_memo !== false && ', Summary Memo'}
                  {importData.deliverables?.include_schedule_c !== false && ', Schedule C'}
                </Typography>
              </Box>
              {packageType === 'Operating Agreement Package - FLP' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  FLP template is preview-quality. Attorney must review every page before delivery.
                </Alert>
              )}
            </Paper>
          )}

          {/* Field Review (read-only) — estate-plan-import only */}
          {!isOA && (
          <Paper elevation={1} sx={{ mb: 2 }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="h6">Document Fields</Typography>
              <Typography variant="body2" color="text.secondary">
                {validation.canGenerate
                  ? 'All required fields are present. Review and generate.'
                  : 'Red fields are missing. Fix the JSON upstream and re-import.'}
              </Typography>
            </Box>
            {FIELD_GROUPS.map((group) => {
              const hasContent = group.fields.some(f =>
                variables[f.key] || missingFieldSet.has(f.key)
              );
              if (!hasContent) return null;

              return (
                <Accordion key={group.label} defaultExpanded={group.fields.some(f => missingFieldSet.has(f.key))} disableGutters elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="medium">{group.label}</Typography>
                    {group.fields.some(f => missingFieldSet.has(f.key)) && (
                      <ErrorIcon color="error" sx={{ ml: 1, fontSize: 18 }} />
                    )}
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {group.fields.map((f) => {
                      const value = variables[f.key];
                      const isMissing = missingFieldSet.has(f.key);
                      if (!value && !isMissing) return null;

                      return (
                        <Box key={f.key} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getFieldColor(f.key), flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ minWidth: 220, color: 'text.secondary' }}>{f.label}:</Typography>
                          <Typography variant="body2" fontWeight={isMissing ? 'bold' : 'normal'} color={isMissing ? 'error' : 'text.primary'}>
                            {value || '(missing)'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
          )}

          {/* Post-execution To-Do List opt-in + matter-facts collector — EP package mode only.
              The To-Do List is package-scoped; in EL-only and PC-only modes it is suppressed
              because those documents are generated standalone before drafting facts are settled. */}
          {!isOA && renderTarget === 'package' && (
          <MatterFactsForm
            packageType={packageType}
            hasSpouse={Boolean(importData?.spouse?.full_name)}
            initialMatterFacts={importData?.matter_facts || {}}
            initialSpouseMatterFacts={importData?.spouse_matter_facts || {}}
            onChange={setTodoListOptions}
          />
          )}

          {/* Generation Error */}
          {genError && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
              <AlertTitle>Generation Failed</AlertTitle>
              {genError}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button variant="outlined" startIcon={<BackIcon />}
              onClick={() => { setImportData(null); setVariables(null); setValidation(null); setPackageType(null); setGenError(null); }}>
              Back to JSON Input
            </Button>

            <Tooltip title={!validation.canGenerate ? 'Fix missing fields in the JSON and re-import' : ''}>
              <span>
                <Button
                  variant="contained" size="large"
                  onClick={handleGenerate}
                  disabled={!validation.canGenerate || generating}
                  startIcon={generating ? <CircularProgress size={20} /> : <CheckIcon />}
                >
                  {generating
                    ? (renderTarget === 'engagement_letter' ? 'Generating Engagement Letter...'
                      : renderTarget === 'planning_confirmation' ? 'Generating Planning Confirmation...'
                      : 'Generating Documents...')
                    : (renderTarget === 'engagement_letter' ? 'Generate Engagement Letter'
                      : renderTarget === 'planning_confirmation' ? 'Generate Planning Confirmation'
                      : 'Generate Documents')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );
}
